import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CreditCard, Landmark, Banknote, Loader2, SkipForward, Wallet } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const labelL = "mb-2 block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500";
const inputL = "w-full border border-zinc-200 bg-white px-4 py-3.5 text-sm text-slate-900 outline-none transition-colors focus:border-blaze";

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

const EXTRA_REASONS = ["Stairs", "Extra Travel", "Waiting Time", "Additional Hours", "Other"];

export const PaymentPanel = ({ job, onDone }) => {
  const [stage, setStage] = useState("choice"); // choice | calc | bank
  const [method, setMethod] = useState("card");
  const [bank, setBank] = useState(null);
  const [hours, setHours] = useState("");
  const [calloutMin, setCalloutMin] = useState("0");
  const [rate, setRate] = useState("");
  const [extrasOn, setExtrasOn] = useState(false);
  const [extras, setExtras] = useState([{ reason: "Stairs", amount: "" }]);
  const [feePaid, setFeePaid] = useState(0);
  const [ref, setRef] = useState("");
  const [collector, setCollector] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/jobs/${job.job_id}/payment`).then(({ data }) => {
      setBank(data.bank);
      setHours(data.defaults.total_hours ? String(data.defaults.total_hours) : "");
      setCalloutMin(String(data.defaults.callout_minutes || 0));
      setRate(data.defaults.rate ? String(data.defaults.rate) : "");
      setFeePaid(data.defaults.booking_fee_paid || 0);
    }).catch(() => {});
  }, [job.job_id]);

  const extrasClean = extrasOn ? extras.filter((e) => num(e.amount) > 0) : [];
  const extrasTotal = extrasClean.reduce((s, e) => s + num(e.amount), 0);
  const jobTotal = useMemo(
    () => Math.round((num(hours) + num(calloutMin) / 60) * num(rate) * 100) / 100,
    [hours, calloutMin, rate]
  );
  const totalJob = Math.round((jobTotal + extrasTotal) * 100) / 100;
  const amountDue = Math.max(Math.round((totalJob - feePaid) * 100) / 100, 0);
  const surcharge = method === "card" ? Math.round(amountDue * 0.025 * 100) / 100 : 0;
  const total = Math.round((amountDue + surcharge) * 100) / 100;

  const setExtra = (i, k, v) => setExtras((xs) => xs.map((e, j) => (j === i ? { ...e, [k]: v } : e)));

  const payload = (m) => ({
    method: m,
    hours: num(hours),
    callout_minutes: num(calloutMin),
    rate: num(rate),
    extras: extrasClean.map((e) => ({ reason: e.reason, amount: num(e.amount) })),
    transaction_ref: ref.trim(),
    collected_by: collector.trim(),
    origin_url: window.location.origin,
  });

  const payCard = async () => {
    setError("");
    if (total <= 0) return setError("Total must be greater than zero.");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/jobs/${job.job_id}/payment/card`, payload("card"));
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(err.response?.data?.detail || "Could not start card payment. Try again.");
      setBusy(false);
    }
  };

  const record = async (m) => {
    setError("");
    if (m === "bank" && !ref.trim()) return setError("Enter the transaction / reference number.");
    if (m !== "skipped" && total <= 0) return setError("Total must be greater than zero.");
    setBusy(true);
    try {
      await axios.post(`${API}/jobs/${job.job_id}/payment`, payload(m));
      if (m === "skipped") toast.success("Payment skipped — $0 collected recorded.");
      else toast.success(`Payment recorded — $${total} via ${m === "bank" ? "bank transfer" : "cash"}. Invoice emailed.`);
      onDone();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not save payment. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const Totals = () => (
    <div className="mt-6 border border-zinc-200 bg-[#f4f1ea]/60 p-5" data-testid="payment-totals">
      <div className="flex justify-between text-sm text-slate-600"><span>Job total ({hours || 0} hrs + {calloutMin || 0} min call-out × ${rate || 0}/hr)</span><span className="font-semibold text-slate-900" data-testid="payment-subtotal">${jobTotal.toFixed(2)}</span></div>
      {extrasClean.map((e, i) => (
        <div key={i} className="mt-2 flex justify-between text-sm text-slate-600"><span>Extra — {e.reason}</span><span className="font-semibold text-slate-900">+${num(e.amount).toFixed(2)}</span></div>
      ))}
      {extrasTotal > 0 && (
        <div className="mt-2 flex justify-between text-sm font-semibold text-slate-900"><span>Total job amount (incl. extras)</span><span data-testid="payment-total-job">${totalJob.toFixed(2)}</span></div>
      )}
      {feePaid > 0 && (
        <>
          <div className="mt-2 flex justify-between text-sm text-green-700"><span>Booking fee already paid</span><span data-testid="payment-fee-paid">-${feePaid.toFixed(2)}</span></div>
          <div className="mt-2 flex justify-between text-sm font-semibold text-slate-900"><span>Final amount due</span><span data-testid="payment-amount-due">${amountDue.toFixed(2)}</span></div>
        </>
      )}
      {method === "card" && (
        <div className="mt-2 flex justify-between text-sm text-slate-600"><span>Card surcharge (2.5%)</span><span className="font-semibold text-slate-900" data-testid="payment-surcharge">${surcharge.toFixed(2)}</span></div>
      )}
      <div className="mt-3 flex justify-between border-t border-zinc-300 pt-3"><span className="font-display text-lg font-semibold uppercase tracking-tight">Total to collect</span><span className="font-display text-2xl font-semibold text-blaze" data-testid="payment-total">${total.toFixed(2)}</span></div>
    </div>
  );

  return (
    <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="border border-zinc-200 bg-white p-8 shadow-sm lg:p-12" data-testid="payment-panel">
      <p className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-blaze">Job {job.job_id}</p>
      <h2 className="font-display text-3xl font-semibold uppercase tracking-tighter text-slate-900 sm:text-4xl">
        Collect <span className="text-blaze">payment</span>
      </h2>

      {stage === "choice" && (
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          <button
            onClick={() => setStage("calc")}
            className="flex flex-col items-center gap-3 border border-blaze bg-blaze px-6 py-8 text-white transition-transform hover:scale-[1.02]"
            data-testid="take-payment-btn"
          >
            <Wallet className="h-8 w-8" strokeWidth={1.6} />
            <span className="font-display text-lg font-semibold uppercase tracking-wide">Take Payment</span>
            <span className="text-xs text-white/80">Card, PayID / bank transfer or cash</span>
          </button>
          <button
            onClick={() => record("skipped")}
            disabled={busy}
            className="flex flex-col items-center gap-3 border border-zinc-300 px-6 py-8 text-slate-700 transition-colors hover:border-blaze hover:text-blaze"
            data-testid="skip-payment-btn"
          >
            <SkipForward className="h-8 w-8" strokeWidth={1.6} />
            <span className="font-display text-lg font-semibold uppercase tracking-wide">Skip Payment</span>
            <span className="text-xs text-slate-500">Direct sign-off — records $0 collected</span>
          </button>
        </div>
      )}

      {stage === "calc" && (
        <div className="mt-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Payment method</p>
          <div className="mt-3 grid grid-cols-3 gap-2" data-testid="payment-methods">
            {[
              ["card", "Card", CreditCard],
              ["bank", "PayID / Bank", Landmark],
              ["cash", "Cash", Banknote],
            ].map(([m, label, Icon]) => (
              <button
                key={m}
                onClick={() => setMethod(m)}
                className={`flex flex-col items-center gap-2 border px-3 py-4 text-xs font-bold uppercase tracking-wider transition-all ${method === m ? "border-blaze bg-blaze text-white" : "border-zinc-300 text-slate-700 hover:border-blaze/60"}`}
                data-testid={`method-${m}`}
              >
                <Icon className="h-5 w-5" /> {label}
              </button>
            ))}
          </div>

          <div className="mt-6 grid gap-5 sm:grid-cols-3">
            <div>
              <label className={labelL}>Working hours</label>
              <input type="number" min="0" step="0.5" className={inputL} value={hours} onChange={(e) => setHours(e.target.value)} data-testid="pay-hours-input" />
            </div>
            <div>
              <label className={labelL}>Call-out (minutes)</label>
              <input type="number" min="0" step="5" className={inputL} value={calloutMin} onChange={(e) => setCalloutMin(e.target.value)} data-testid="pay-callout-input" />
            </div>
            <div>
              <label className={labelL}>Rate ($/hr)</label>
              <input type="number" min="0" step="1" className={inputL} value={rate} onChange={(e) => setRate(e.target.value)} data-testid="pay-rate-input" />
            </div>
          </div>
          <p className="mt-2 text-[10px] text-slate-400">Picked automatically from the job — adjust here if needed.</p>

          <div className="mt-5">
            <label className={labelL}>Collected by (your name)</label>
            <input className={inputL} value={collector} onChange={(e) => setCollector(e.target.value)} placeholder="e.g. Shozab" data-testid="pay-collector-input" />
          </div>

          <div className="mt-6 border-t border-zinc-200 pt-5" data-testid="extras-section">
            <label className={labelL}>Extra charges</label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setExtrasOn(true)} className={`border px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-all ${extrasOn ? "border-blaze bg-blaze text-white" : "border-zinc-300 text-slate-700"}`} data-testid="extras-yes">Yes</button>
              <button type="button" onClick={() => setExtrasOn(false)} className={`border px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-all ${!extrasOn ? "border-blaze bg-blaze text-white" : "border-zinc-300 text-slate-700"}`} data-testid="extras-no">No</button>
            </div>
            {extrasOn && (
              <div className="mt-4 space-y-3" data-testid="extras-list">
                {extras.map((e, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <select className={`${inputL} flex-1`} value={e.reason} onChange={(ev) => setExtra(i, "reason", ev.target.value)} data-testid={`extra-reason-${i}`}>
                      {EXTRA_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <input type="number" min="0" step="1" className={`${inputL} w-28`} placeholder="$" value={e.amount} onChange={(ev) => setExtra(i, "amount", ev.target.value)} data-testid={`extra-amount-${i}`} />
                    {extras.length > 1 && (
                      <button type="button" onClick={() => setExtras((xs) => xs.filter((_, j) => j !== i))} className="text-xs font-bold text-slate-400 hover:text-blaze" data-testid={`extra-remove-${i}`}>✕</button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={() => setExtras((xs) => [...xs, { reason: "Extra Travel", amount: "" }])} className="text-xs font-bold uppercase tracking-widest text-blaze" data-testid="extra-add">+ Add another charge</button>
              </div>
            )}
          </div>

          <Totals />

          {method === "card" && (
            <button onClick={payCard} disabled={busy} className="mt-6 flex w-full items-center justify-center gap-2 bg-blaze px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white disabled:opacity-50" data-testid="pay-card-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Pay ${total.toFixed(2)} by Card
            </button>
          )}
          {method === "bank" && (
            <button onClick={() => setStage("bank")} disabled={busy} className="mt-6 flex w-full items-center justify-center gap-2 bg-blaze px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white disabled:opacity-50" data-testid="pay-bank-continue-btn">
              Continue — PayID / Bank Transfer
            </button>
          )}
          {method === "cash" && (
            <button onClick={() => record("cash")} disabled={busy} className="mt-6 flex w-full items-center justify-center gap-2 bg-blaze px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white disabled:opacity-50" data-testid="pay-cash-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Banknote className="h-4 w-4" />} Confirm Cash Payment — ${total.toFixed(2)}
            </button>
          )}
          <button onClick={() => setStage("choice")} className="mt-3 w-full text-center text-xs font-semibold uppercase tracking-widest text-slate-400 hover:text-blaze" data-testid="payment-back-btn">Back</button>
        </div>
      )}

      {stage === "bank" && bank && (
        <div className="mt-8">
          <div className="border border-zinc-200 bg-[#f4f1ea]/60 p-6" data-testid="bank-details">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Transfer ${total.toFixed(2)} to</p>
            <div className="mt-4 space-y-2 text-sm">
              <p className="flex justify-between"><span className="text-slate-500">Account Name</span><span className="font-bold text-slate-900">{bank.account_name}</span></p>
              <p className="flex justify-between"><span className="text-slate-500">BSB</span><span className="font-bold text-slate-900">{bank.bsb}</span></p>
              <p className="flex justify-between"><span className="text-slate-500">Account Number</span><span className="font-bold text-slate-900">{bank.account_number}</span></p>
              <p className="flex justify-between border-t border-zinc-300 pt-2"><span className="text-slate-500">Amount</span><span className="font-display text-lg font-semibold text-blaze">${total.toFixed(2)}</span></p>
            </div>
          </div>
          <div className="mt-5">
            <label className={labelL}>Transaction / Reference Number (once received)</label>
            <input className={inputL} value={ref} onChange={(e) => setRef(e.target.value)} placeholder="e.g. PayID ref 8XJ2K9" data-testid="pay-ref-input" />
          </div>
          <button onClick={() => record("bank")} disabled={busy} className="mt-6 flex w-full items-center justify-center gap-2 bg-blaze px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white disabled:opacity-50" data-testid="pay-bank-confirm-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Landmark className="h-4 w-4" />} Confirm Payment Received — ${total.toFixed(2)}
          </button>
          <button onClick={() => setStage("calc")} className="mt-3 w-full text-center text-xs font-semibold uppercase tracking-widest text-slate-400 hover:text-blaze" data-testid="bank-back-btn">Back</button>
        </div>
      )}

      {error && <p className="mt-5 text-sm font-semibold text-blaze" data-testid="payment-error">{error}</p>}
      <p className="mt-6 text-center text-[10px] text-slate-400">Payment details are recorded against this job and visible to the office.</p>
    </motion.div>
  );
};
